/**
 * Climatiq unit-type support.
 *
 * Source of truth mapping every Climatiq `unit_type`
 * (https://www.climatiq.io/docs/api-reference/unit-types) to:
 *  - the `/estimate` parameters to send at quantity 1, so the returned `co2e`
 *    IS the per-unit emission factor (kg CO2e per `inputUnitCode`), and
 *  - the canonical `input_unit_code` we store on the factor — the unit the user
 *    enters the activity quantity in.
 *
 * "Simple" types resolve to a library-convertible base unit (kg/km/kWh/l/m2),
 * so the form's unit dropdown can offer compatible alternatives. Compound or
 * non-physical types (per-passenger-distance, currency, count, freight tonne-km,
 * *-over-time) resolve to an OPAQUE code (equivalence-only, never converted) —
 * these are registered in OPAQUE_UNIT_CODES in apps/web's lib/unitConversion.ts.
 *
 * Shared between apps/web (emission-factor picker, activity form) and apps/api
 * (bookkeeper assistant save-factor tool) so both build identical factor
 * snapshots — the save RPC's deterministic factor_code depends on it.
 */
const m = (inputUnitCode, estimateParameters, opaque = false) => ({ inputUnitCode, estimateParameters, outputUnitCode: 'kg', opaque });
/** Keyed by the exact `unit_type` string Climatiq returns on search results. */
export const CLIMATIQ_UNIT_TYPES = {
    // Simple, library-convertible
    Weight: m('kg', { weight: 1, weight_unit: 'kg' }),
    Distance: m('km', { distance: 1, distance_unit: 'km' }),
    Energy: m('kWh', { energy: 1, energy_unit: 'kWh' }),
    Volume: m('l', { volume: 1, volume_unit: 'l' }),
    Area: m('m2', { area: 1, area_unit: 'm2' }),
    // hour/kW/GB are library-known (or passthrough) unit codes, not registered
    // opaque codes — opaque:false keeps the `opaque` flag truthful (see the
    // isOpaque(inputUnitCode) === opaque invariant enforced in the test).
    Time: m('hour', { time: 1, time_unit: 'hour' }),
    Power: m('kW', { power: 1, power_unit: 'kW' }),
    Data: m('GB', { data: 1, data_unit: 'GB' }),
    // Non-physical
    Money: m('eur', { money: 1, money_unit: 'eur' }, true),
    Number: m('number', { number: 1 }, true),
    // Compound (single-magnitude for the activity: the compound IS one number)
    PassengerOverDistance: m('passenger_km', { distance: 1, distance_unit: 'km' }, true),
    ContainerOverDistance: m('container_km', { distance: 1, distance_unit: 'km' }, true),
    // Compound (two magnitudes — estimate both at 1 to get the per-(unit·unit) factor)
    WeightOverDistance: m('tonne_km', { weight: 1, weight_unit: 't', distance: 1, distance_unit: 'km' }, true),
    VolumeOverDistance: m('litre_km', { volume: 1, volume_unit: 'l', distance: 1, distance_unit: 'km' }, true),
    AreaOverTime: m('m2_year', { area: 1, area_unit: 'm2', time: 1, time_unit: 'year' }, true),
    DataOverTime: m('gb_hour', { data: 1, data_unit: 'GB', time: 1, time_unit: 'hour' }, true),
    DistanceOverTime: m('km_hour', { distance: 1, distance_unit: 'km', time: 1, time_unit: 'hour' }, true),
    WeightOverTime: m('kg_year', { weight: 1, weight_unit: 'kg', time: 1, time_unit: 'year' }, true),
    NumberOverTime: m('number_year', { number: 1, time: 1, time_unit: 'year' }, true),
};
/** Lookup with a safe fallback (unknown type → treat the value as per-kg). */
export function climatiqUnitTypeMapping(unitType) {
    return (unitType && CLIMATIQ_UNIT_TYPES[unitType]) || CLIMATIQ_UNIT_TYPES.Weight;
}
export function isSupportedClimatiqUnitType(unitType) {
    return !!unitType && unitType in CLIMATIQ_UNIT_TYPES;
}
/** Pretty-print a Climatiq source_lca_activity (e.g. "well_to_wheel" → "Well-to-wheel"). */
export function prettyLca(v) {
    if (typeof v !== "string" || !v)
        return "";
    return v.split("_").join("-").replace(/^./, (c) => c.toUpperCase());
}
/**
 * Slug used for the `category` dimension when snapshotting a Climatiq factor
 * (lowercase; any run of non-alphanumerics → '_'). Must stay identical between
 * the web activity form and the bookkeeper save tool: the save RPC derives the
 * deterministic factor_code from the dimension values, so divergent slugs would
 * duplicate rows instead of deduping.
 */
export function slugifyClimatiqDimension(s) {
    return s.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}
//# sourceMappingURL=climatiqUnitTypes.js.map